/**
 * `<page-approvals>` — one instance's approval inbox, routed at
 * `/instances/:id/approvals`.
 *
 * Implements `SC-F810` over the two routes of `06` §3.14. Story: `SC-S906`'s
 * custody criterion is the one this screen has to honour, and `SC-S907` is why
 * every state on it has designed copy.
 *
 * # This screen is the M5 exit criterion
 *
 * "An approval is granted end to end in the UI with no capability URL leaving our
 * custody." An approver signs in, sees the queries their `ApprovalPolicy` is
 * holding, and decides — against an authenticated session, with a role check and
 * a step-up grant, on a screen that never displays, copies or links a capability.
 *
 * The capability URLs Sluice delivered are sealed in the control-plane database
 * and the API has no field for them, so there is nothing here to leak: the
 * decision is a `POST` to our own route and the server replays it. That is the
 * `SLUICE-U11` fallback and the page says so out loud, in the panel built from
 * `limits.notes`, because a reader who does not know a capability is being held on
 * their behalf cannot reason about the risk.
 *
 * # The asymmetry, as an interface
 *
 * **Approve** opens the verification dialog. **Reject** does not. That is the
 * whole point of the asymmetry `01` §3.3 draws — a denial reduces authority — and
 * putting a dialog in front of both would teach an approver to click through the
 * one that matters. The reason field is offered on both and is optional on both,
 * because "why did you say no" is asked as often as its opposite, and it is
 * recorded in the organization's activity feed rather than on the instance: Sluice's
 * capability endpoint accepts no reason.
 *
 * # Four states, all designed
 *
 * Loading is a table spinner. Empty distinguishes three facts — no receiver
 * configured, a filter that matches nothing, and a genuinely quiet instance —
 * because they need three different next actions. Error names the `apierr` code
 * and the `request_id`. Populated is the queue, pending first by recency.
 *
 * There is no polling. An approval is a human handoff on a fifteen-minute clock,
 * not a job with a progress bar: a Refresh button an approver presses when they
 * arrive is honest about what the page knows, and a five-second poll on a screen
 * somebody leaves open all day is a request every five seconds for a queue that
 * changes a few times a week. The countdown beside each row is computed from the
 * deadline on a one-minute tick, which is local arithmetic and no traffic.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTable,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  APPROVAL_STATES,
  APPROVAL_STATE_LABELS,
  decideApproval,
  listApprovals,
  remaining,
  type ApprovalDecision,
  type ApprovalLimits,
  type ApprovalRequest,
  type ApprovalState,
} from "../api/approvals.js";
import {
  confirmStepUpOtp,
  listPasskeys,
  requestStepUpOtp,
  verifyWithPasskey,
} from "../api/auth.js";
import { passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";
import { getInstance, type Instance } from "../api/instances.js";
import { hrefFor, navigate } from "../nav.js";

/** The page size. Fifty is the API's own default and the queue is keyset-paged. */
const PAGE_LIMIT = 50;

/** How often the countdown column re-renders. One minute: the values are rendered
 * to the minute above a minute, so a faster tick would repaint the same string. */
const TICK_MS = 60_000;

const COLUMNS: BnColumn[] = [
  { key: "state", label: "State", width: "1px" },
  { key: "subject", label: "Subject" },
  { key: "statement", label: "Statement" },
  { key: "policies", label: "Held by" },
  { key: "clock", label: "Deadline", width: "1px" },
  { key: "actions", label: "", width: "1px", align: "right" },
];

@customElement("page-approvals")
export class PageApprovals extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private rows: ApprovalRequest[] = [];
  @state() private next: string | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  @state() private limits: ApprovalLimits | null = null;
  @state() private instance: Instance | null = null;

  /** The `?state=` filter. Empty is "no filter", which the query builder drops. */
  @state() private filter: ApprovalState | "" = "pending";

  /** The request the decision dialog is about, and which way it is pointing. */
  @state() private target: ApprovalRequest | null = null;
  @state() private decision: ApprovalDecision = "approve";
  @state() private reason = "";
  @state() private submitting = false;

  /** Step-up sub-state. It is reset on every open: codes and grants are
   * single-use, so anything left from a previous round could only produce
   * `otp_invalid`. */
  @state() private verifying = false;
  @state() private needsVerify = false;
  @state() private otpId = "";
  @state() private otpCode = "";
  /** Null means "not looked up yet", which keeps the passkey branch visible in an
   * unknown state and retries on click — hiding a working method because one
   * lookup failed would be worse (`SC-S012`). */
  @state() private hasPasskeys: boolean | null = null;

  /** Forces the countdown column to re-render. */
  @state() private tick = 0;
  private timer?: number;

  /** True once a filter other than the default is applied, so the empty state can
   * tell "nothing matches this filter" from "nothing is waiting". */
  private get filtered(): boolean {
    return this.filter !== "";
  }

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-md);
      max-width: 62ch;
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar-end {
      margin-left: auto;
      display: flex;
      gap: var(--bino-space-sm);
    }
    .sub {
      display: block;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    .mono {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      overflow-wrap: anywhere;
    }
    .statement {
      display: block;
      max-width: 44ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
    }
    .clipped {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    .row-actions {
      display: inline-flex;
      gap: var(--bino-space-xs);
    }
    .copy {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-md);
      max-width: 74ch;
    }
    .copy:last-child {
      margin-bottom: 0;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .verify-branch {
      margin-bottom: var(--bino-space-sm);
    }
    .verify-or {
      margin: 0 0 var(--bino-space-sm);
      text-align: center;
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    .notes {
      margin: 0;
      padding-left: var(--bino-space-lg);
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-md);
    }
    .notes li + li {
      margin-top: var(--bino-space-xs);
    }
    .dialog-statement {
      margin-bottom: var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.timer = window.setInterval(() => (this.tick += 1), TICK_MS);
  }

  override disconnectedCallback() {
    if (this.timer !== undefined) window.clearInterval(this.timer);
    super.disconnectedCallback();
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) {
      this.rows = [];
      this.next = null;
      this.instance = null;
      if (this.instanceId) {
        void this.load(null);
        void this.loadInstance();
      }
    }
  }

  /** load fetches one page. `cursor` is the API's own opaque `next`, never
   * parsed. */
  private async load(cursor: string | null) {
    this.loading = true;
    try {
      const page = await listApprovals(this.instanceId, {
        limit: PAGE_LIMIT,
        cursor: cursor ?? undefined,
        state: this.filter || undefined,
      });
      this.rows = page.items;
      this.next = page.next;
      this.limits = page.limits;
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.rows = [];
      this.next = null;
    } finally {
      this.loading = false;
    }
  }

  /** The instance's name for the heading. Best-effort: this page is about the
   * queue, and an unreadable instance record must not blank it. */
  private async loadInstance() {
    try {
      this.instance = await getInstance(this.instanceId);
    } catch {
      this.instance = null;
    }
  }

  private onFilterChange() {
    this.rows = [];
    this.next = null;
    this.renderRoot.querySelector<BnTable>("bn-table")?.resetPagination();
    void this.load(null);
  }

  /* ── the decision ────────────────────────────────────────────────────── */

  /**
   * open puts the dialog up. `needsVerify` is decided HERE, from the decision and
   * the server's own statement of the asymmetry — not from a constant in this file,
   * so a deployment that disabled the gate does not render a dialog that asks for
   * a grant nothing will check.
   */
  private open(row: ApprovalRequest, decision: ApprovalDecision) {
    this.target = row;
    this.decision = decision;
    this.reason = "";
    this.otpId = "";
    this.otpCode = "";
    this.needsVerify = decision === "approve" && (this.limits?.grant_requires_step_up ?? true);
    if (this.needsVerify) void this.ensurePasskeyInfo();
  }

  private close() {
    if (this.submitting || this.verifying) return;
    this.target = null;
  }

  /** submit sends the decision. `verification` is present only on the approve
   * path; the server ignores it on the other. */
  private async submit(verification?: string) {
    const row = this.target;
    if (!row || this.submitting) return;
    if (this.needsVerify && !verification) return;
    this.submitting = true;
    try {
      const decided = await decideApproval(this.instanceId, row.approval_id, this.decision, {
        reason: this.reason.trim(),
        verification,
      });
      // Patch the row in place rather than refetching: the queue's order is
      // newest-first by `(created, id)` and neither moves, so a refetch would
      // scroll the list under somebody who is working down it.
      this.rows = this.rows.map((r) => (r.approval_id === decided.approval_id ? decided : r));
      this.target = null;
      toast(
        this.decision === "approve"
          ? "Approved. The query that was waiting can run."
          : "Rejected. The query failed with ERR_APPROVAL_REJECTED.",
        "success",
      );
      // A decision changes what belongs in the default `pending` view, so the page
      // is re-read once the toast is up.
      if (this.filter === "pending") void this.load(null);
    } catch (err) {
      this.decisionFailed(parseError(err));
    } finally {
      this.submitting = false;
    }
  }

  /**
   * decisionFailed routes a failure.
   *
   * A spent or invalid grant re-opens the verification step rather than closing
   * the dialog, which is the difference between "try again" and "start over". A
   * conflict is terminal and the row is refreshed, because the truth is now
   * different from what the screen showed — somebody else decided it, or the
   * window closed while the dialog was open.
   */
  private decisionFailed(err: ApiError) {
    if (err.code === "verification_required" || err.code === "verification_invalid") {
      this.needsVerify = true;
      this.otpId = "";
      this.otpCode = "";
      toast(err.message, "error");
      return;
    }
    if (err.code === "conflict") {
      this.target = null;
      toast(err.message, "warning");
      void this.load(null);
      return;
    }
    toast(`${err.message} (${err.code}${err.requestId ? ` · ${err.requestId}` : ""})`, "error");
  }

  /* ── step-up (`SC-F011`) ─────────────────────────────────────────────── */

  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Deliberately left null; see the field's doc comment.
    }
  }

  private async verifyByPasskey() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      await this.ensurePasskeyInfo();
      const grant = await verifyWithPasskey({
        action: "approve_query",
        target: this.instanceId,
      });
      this.needsVerify = false;
      await this.submit(grant);
    } catch (err) {
      // A dismissed authenticator prompt is a cancellation, not a failure: the
      // dialog stays open, nothing is said, and the email branch is still there.
      const message = passkeyFailureMessage(err);
      if (message) toast(message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async sendCode() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.otpCode = "";
    } catch (err) {
      toast(parseError(err).message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async confirmCode() {
    const code = this.otpCode.trim();
    if (code === "" || this.otpId === "" || this.verifying) return;
    this.verifying = true;
    try {
      const grant = await confirmStepUpOtp(this.otpId, code, {
        action: "approve_query",
        target: this.instanceId,
      });
      this.needsVerify = false;
      await this.submit(grant);
    } catch (err) {
      // The server answers one uniform `otp_invalid` for wrong, expired and
      // foreign codes alike, so there is exactly one failure path to render.
      toast(parseError(err).message, "error");
    } finally {
      this.verifying = false;
    }
  }

  /* ── rendering ───────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private filterOptions(): BnSelectOption[] {
    return [
      { value: "", label: "Everything" },
      ...APPROVAL_STATES.map((s) => ({
        value: s,
        label: APPROVAL_STATE_LABELS[s],
      })),
    ];
  }

  private toRows(): BnRow[] {
    // `this.tick` is read so the countdown column re-renders on the interval.
    void this.tick;
    return this.rows.map((r) => ({
      id: r.id,
      state: html`<bn-badge variant=${stateVariant(r.state)} ?dot=${r.state === "pending"}>
          ${APPROVAL_STATE_LABELS[r.state]}
        </bn-badge>
        ${r.explanation ? html`<span class="sub">${r.explanation}</span>` : nothing}`,
      subject: html`<span class="mono">${r.subject_id || "—"}</span>
        <span class="sub">${r.approval_id}</span>`,
      statement: r.sql_sample
        ? html`<span class="statement" title=${r.sql_sample}>${r.sql_sample}</span> ${
              r.sql_truncated
                ? html`<span class="clipped">clipped — the full statement is longer</span>`
                : nothing
            }`
        : html`<span class="sub"
            >no statement sample — this instance sends none (approval.sqlSampleBytes)</span
          >`,
      policies: r.policies.length
        ? html`${r.policies.join(", ")}`
        : html`<span class="sub">—</span>`,
      clock:
        r.state === "pending"
          ? html`<span>${remaining(r.expires_at)}</span>
              <span class="sub">${when(r.requested_at)}</span>`
          : html`<span>${when(r.decided_at ?? r.expires_at)}</span> ${
                r.decided_by_label
                  ? html`<span class="sub">by ${r.decided_by_label}</span>`
                  : r.decided_by
                    ? html`<span class="sub">by a former colleague</span>`
                    : nothing
              }`,
      actions: r.decidable
        ? html`<span class="row-actions">
            <bn-button size="sm" @click=${() => this.open(r, "reject")}>Reject</bn-button>
            <bn-button size="sm" variant="primary" @click=${() => this.open(r, "approve")}>
              Approve
            </bn-button>
          </span>`
        : html`<span class="sub">—</span>`,
    }));
  }

  /** Three empty states, because they are three different facts (`SC-S907`). */
  private renderEmpty(): TemplateResult {
    if (this.limits && !this.limits.receiver_configured) {
      return html`<bn-empty-state
        icon="⚠"
        heading="This deployment has no approval receiver configured"
        message="Sluice delivers approval requests only by webhook, and Sluice Cloud has not been told its own public address — so no instance is being told where to deliver. An ApprovalPolicy will hold every matching query until it expires and nothing will appear here. This is a deployment setting, not something you can fix from this screen."
      ></bn-empty-state>`;
    }
    if (this.filtered) {
      return html`<bn-empty-state
        icon="⌕"
        heading=${`Nothing is ${APPROVAL_STATE_LABELS[this.filter as ApprovalState].toLowerCase()}`}
        message="This instance has raised approvals, but none in this state. Clearing the filter shows the whole history."
      >
        <bn-button
          slot="actions"
          variant="primary"
          @click=${() => {
            this.filter = "";
            this.onFilterChange();
          }}
        >
          Show everything
        </bn-button>
      </bn-empty-state>`;
    }
    return html`<bn-empty-state
      icon="✓"
      heading="No query is waiting for approval"
      message="An ApprovalPolicy holds a matching query until somebody here decides. Nothing has matched one on this instance, which is the state most instances are in most of the time."
    >
      <bn-button
        slot="actions"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "policies"))}
      >
        Review the policies
      </bn-button>
    </bn-empty-state>`;
  }

  private renderDialog() {
    const row = this.target;
    const approving = this.decision === "approve";
    const sent = this.otpId !== "";
    const offerPasskey = this.hasPasskeys !== false;
    return html`<bn-modal
      ?open=${row !== null}
      tone=${approving ? "default" : "destructive"}
      heading=${approving ? "Approve this query?" : "Reject this query?"}
      @close=${() => this.close()}
    >
      ${
        row
          ? html`
              <p class="copy">
                ${
                  approving
                    ? html`<strong class="mono">${row.subject_id}</strong> will be allowed to run
                        this statement once. Sluice releases the query it is holding and the grant
                        is spent by that query — there is no undo.`
                    : html`<strong class="mono">${row.subject_id}</strong> will be told no. The
                        query fails with <code>ERR_APPROVAL_REJECTED</code> and nothing runs.`
                }
              </p>
              ${
                row.sql_sample
                  ? html`<div class="dialog-statement">
                      <bn-code
                        language="sql"
                        label="The statement waiting for approval"
                        .value=${row.sql_sample}
                      ></bn-code>
                      ${
                      row.sql_truncated
                        ? html`<p class="copy">
                            This sample is clipped at the size the instance was configured to send.
                            You are not looking at the whole statement.
                          </p>`
                        : nothing
                    }
                    </div>`
                  : nothing
              }
              <bn-field
                label="Reason"
                hint="Optional, and recorded in this organization's activity feed. It is not sent to the instance: Sluice's approval endpoint accepts no reason."
              >
                <bn-input
                  placeholder="INC-1234: verified with the data owner"
                  ?disabled=${this.submitting || this.verifying}
                  .value=${this.reason}
                  @input=${(e: CustomEvent<{ value: string }>) => (this.reason = e.detail.value)}
                ></bn-input>
              </bn-field>
              ${
                this.needsVerify
                  ? html`
                      <p class="copy">
                        ${
                        sent
                          ? "We sent a one-time code to your email address. Enter it to approve."
                          : "Approving releases a query your policy refused, against live data, so it needs a fresh check that you are here. The proof is single-use and valid for five minutes. Rejecting needs none."
                      }
                      </p>
                      ${
                      offerPasskey
                        ? html`<div class="verify-branch">
                              <bn-button
                                variant="secondary"
                                block
                                ?loading=${this.verifying}
                                @click=${() => void this.verifyByPasskey()}
                                >Verify with a passkey</bn-button
                              >
                            </div>
                            <p class="verify-or">or</p>`
                        : nothing
                    }
                      ${
                      sent
                        ? html`<bn-field label="One-time code" required>
                            <bn-input
                              mono
                              inputmode="numeric"
                              autocomplete="one-time-code"
                              placeholder="123456"
                              ?disabled=${this.verifying}
                              .value=${this.otpCode}
                              @input=${(e: CustomEvent<{ value: string }>) =>
                              (this.otpCode = e.detail.value)}
                              @keydown=${(e: KeyboardEvent) => {
                              if (e.key !== "Enter") return;
                              e.preventDefault();
                              void this.confirmCode();
                            }}
                            ></bn-input>
                          </bn-field>`
                        : nothing
                    }
                    `
                  : nothing
              }
            `
          : nothing
      }
      <bn-button slot="footer" variant="ghost" @click=${() => this.close()}>Cancel</bn-button>
      ${
        this.needsVerify
          ? sent
            ? html`<bn-button
                slot="footer"
                variant="primary"
                ?loading=${this.verifying || this.submitting}
                ?disabled=${this.otpCode.trim() === ""}
                @click=${() => void this.confirmCode()}
                >Verify and approve</bn-button
              >`
            : html`<bn-button
                slot="footer"
                variant="primary"
                ?loading=${this.verifying}
                @click=${() => void this.sendCode()}
                >Email me a code</bn-button
              >`
          : html`<bn-button
              slot="footer"
              variant=${approving ? "primary" : "danger"}
              ?loading=${this.submitting}
              @click=${() => void this.submit()}
              >${approving ? "Approve" : "Reject"}</bn-button
            >`
      }
    </bn-modal>`;
  }

  /** The custody panel. It is built from the server's own `limits.notes` rather
   * than from copy in this file, so the page cannot claim a posture the API does
   * not report. */
  private renderCustody() {
    if (!this.limits) return nothing;
    return html`<bn-panel eyebrow="How a decision reaches the instance">
      <ul class="notes">
        ${this.limits.notes.map((n) => html`<li>${n}</li>`)}
      </ul>
    </bn-panel>`;
  }

  override render() {
    if (this.failure) {
      return html`
        <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="The approval queue could not be read">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            ${
              this.failure.code === "validation_failed"
                ? html`<p>
                    The state filter is an allowlist: a value outside
                    <code>pending</code>, <code>approved</code>, <code>rejected</code> and
                    <code>expired</code> is refused rather than ignored. Clear the filter and try
                    again.
                  </p>`
                : nothing
            }
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load(null)}
                >Try again</bn-button
              >
            </div>
          </div>
        </bn-card>
      `;
    }

    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Approvals</h1>
          <p class="lede">
            Queries that ${this.instance?.name ?? "this instance"} is holding until a human decides.
            Approving releases one query, once; rejecting fails it. Both are recorded in this
            organization's activity feed with your name on them.
          </p>
        </div>
      </header>

      <div class="stack">
        <bn-card flush>
          <div class="toolbar" style="padding: var(--bino-space-md) var(--bino-space-md) 0">
            <bn-field label="State">
              <bn-select
                .options=${this.filterOptions()}
                .value=${this.filter}
                placeholder="Everything"
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
                  this.filter = e.detail.value as ApprovalState | "";
                  this.onFilterChange();
                }}
              ></bn-select>
            </bn-field>
            <div class="toolbar-end">
              <bn-button @click=${() => void this.load(null)}>Refresh</bn-button>
            </div>
          </div>
          <bn-table
            caption=${`Approval queue for ${this.instance?.name ?? "this instance"}`}
            .columns=${COLUMNS}
            .rows=${this.toRows()}
            .nextCursor=${this.next}
            paginated
            ?loading=${this.loading}
            @page-change=${(e: CustomEvent<{ cursor: string | null }>) =>
              void this.load(e.detail.cursor)}
          >
            <div slot="empty">${this.renderEmpty()}</div>
          </bn-table>
        </bn-card>

        ${this.renderCustody()}
      </div>

      ${this.renderDialog()}
    `;
  }
}

/** State → badge tone. `expired` is a warning rather than an error: nothing broke,
 * a window closed. */
function stateVariant(state: ApprovalState): BnBadgeVariant {
  switch (state) {
    case "approved":
      return "success";
    case "rejected":
      return "error";
    case "expired":
      return "warning";
    default:
      return "info";
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-approvals": PageApprovals;
  }
}
